import { useState, useEffect, useRef, useId } from 'react'
import { Link } from 'react-router-dom'

export default function TestimonialsSlider({ testimonials = [] }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [columns, setColumns] = useState(3)
  const sliderId = useId()
  const touchStartX = useRef(0)
  const touchEndX = useRef(0)

  // Track responsive columns
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 1024) {
        setColumns(3)
      } else if (window.innerWidth >= 640) {
        setColumns(2)
      } else {
        setColumns(1)
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const items = testimonials.length > 0 ? testimonials : []
  const maxIndex = Math.max(0, items.length - columns)

  // Autoplay loop
  useEffect(() => {
    if (isPaused || items.length <= columns) return
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1))
    }, 6000)
    return () => clearInterval(interval)
  }, [isPaused, maxIndex, items.length, columns])

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1))
  }

  const handleNext = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1))
  }

  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX
  }

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX
  }

  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 50) {
      handleNext()
    }
    if (touchStartX.current - touchEndX.current < -50) {
      handlePrev()
    }
  }

  if (!items || items.length === 0) return null

  // Calculate slide percentage width
  const slideWidth = 100 / columns

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Navigation arrows */}
      <div className="flex items-center justify-end mb-6 sm:mb-8">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrev}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D2DEC7] bg-white text-[#384830] transition hover:bg-[#EAF1E3] hover:text-[#1F2A1A] focus:outline-none focus:ring-2 focus:ring-[#556B48] shadow-xs"
            aria-label="Forrige anmeldelse"
          >
            <i className="fa-solid fa-chevron-left text-xs" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D2DEC7] bg-white text-[#384830] transition hover:bg-[#EAF1E3] hover:text-[#1F2A1A] focus:outline-none focus:ring-2 focus:ring-[#556B48] shadow-xs"
            aria-label="Næste anmeldelse"
          >
            <i className="fa-solid fa-chevron-right text-xs" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Carousel Track */}
      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{
            transform: `translateX(-${currentIndex * slideWidth}%)`,
          }}
        >
          {items.map((item, idx) => (
            <div
              key={item.id || idx}
              className="px-2.5 sm:px-3 flex-shrink-0"
              style={{ width: `${slideWidth}%` }}
            >
              <div className="h-full flex flex-col justify-between rounded-2xl border border-[#DFE6D5] bg-white p-6 sm:p-7 shadow-xs hover:shadow-md transition-shadow duration-300">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-1.5">
                      <div className="flex text-[#F5D154] text-xs gap-0.5" aria-label="5 ud af 5 stjerner">
                        <i className="fa-solid fa-star" aria-hidden="true" />
                        <i className="fa-solid fa-star" aria-hidden="true" />
                        <i className="fa-solid fa-star" aria-hidden="true" />
                        <i className="fa-solid fa-star" aria-hidden="true" />
                        <i className="fa-solid fa-star" aria-hidden="true" />
                      </div>
                    </div>
                    {item.category && (
                      <span className="rounded-full bg-[#FFF7E6] border border-[#FDE6B5] px-3 py-1 text-[11px] font-medium text-[#996515]">
                        {item.category}
                      </span>
                    )}
                  </div>

                  <div
                    className="prose prose-sm text-[#3E4D38] text-sm leading-relaxed line-clamp-6"
                    dangerouslySetInnerHTML={{ __html: item.quote }}
                  />
                </div>

                <div className="border-t border-[#E7EEDF] pt-4 mt-5 flex items-center justify-between">
                  <div>
                    <h4 className="font-serif text-base font-medium text-[#2B3824]">
                      {item.name}
                    </h4>
                    <p className="text-[11px] text-[#6B7F62]">Klient hos Trine Rosendal</p>
                  </div>
                  <Link
                    to="/referencer"
                    className="text-xs font-medium text-[#5C7A4F] hover:text-[#222E1D] flex items-center gap-1"
                  >
                    <span>Læs mere</span>
                    <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dots Indicator */}
      <div className="flex items-center justify-center gap-1.5 mt-6 sm:mt-8">
        {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
          <button
            key={`${sliderId}-dot-${idx}`}
            type="button"
            onClick={() => setCurrentIndex(idx)}
            className={`h-2 rounded-full transition-all duration-300 ${
              currentIndex === idx
                ? 'w-6 bg-[#5C7A4F]'
                : 'w-2 bg-[#DCCBD7] hover:bg-[#B99EB4]'
            }`}
            aria-label={`Gå til side ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  )
}
